import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Users,
  UserCheck,
  BookOpen,
  CreditCard,
  Megaphone,
  Calendar,
  MessageCircle,
  ScanLine,
  Settings,
  Euro,
  Clock,
  UserX,
  UserPlus,
  Dumbbell
} from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';

const AdminDashboardPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const [pendingChats, setPendingChats] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    monthlyRevenue: 0,
    completedClasses: 0,
    waitlistTotal: 0
  });

  // Obtener fechas del mes actual
  const getMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0],
      today: now.toISOString().split('T')[0]
    };
  };

  // Cargar estadísticas y chats pendientes
  useEffect(() => {
    if (!businessId) return;

    const loadData = async () => {
      try {
        const { start, end, today } = getMonthRange();

        // 1. Contar usuarios totales
        const usersQuery = query(
          collection(db, 'users'),
          where('businessId', '==', businessId)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const users = usersSnapshot.docs.map(doc => doc.data());

        // 2. Calcular ingresos del mes (membresías + bonos asignados este mes)
        let monthlyRevenue = 0;

        // Ingresos de membresías activas asignadas este mes
        const userMembershipsQuery = query(
          collection(db, 'userMemberships'),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const membershipsSnapshot = await getDocs(userMembershipsQuery);

        // Cargar precios de membresías
        const membershipsQuery = query(
          collection(db, 'memberships'),
          where('businessId', '==', businessId)
        );
        const membershipTypesSnapshot = await getDocs(membershipsQuery);
        const membershipPrices: { [id: string]: number } = {};
        membershipTypesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          membershipPrices[doc.id] = data.price || 0;
        });

        membershipsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          // Verificar si fue creada/asignada este mes
          // Manejar tanto Date como Timestamp de Firestore
          let startDateStr: string | undefined;
          if (data.startDate) {
            if (typeof data.startDate === 'string') {
              startDateStr = data.startDate.split('T')[0];
            } else if (data.startDate.toDate) {
              startDateStr = data.startDate.toDate().toISOString().split('T')[0];
            } else if (data.startDate instanceof Date) {
              startDateStr = data.startDate.toISOString().split('T')[0];
            }
          } else if (data.createdAt?.toDate) {
            startDateStr = data.createdAt.toDate().toISOString().split('T')[0];
          }

          if (startDateStr && startDateStr >= start && startDateStr <= end) {
            // Usar precio guardado directamente, o buscar en membershipPrices como fallback
            const price = data.price || membershipPrices[data.membershipId] || 0;
            monthlyRevenue += price;
          }
        });

        // Ingresos de bonos asignados este mes
        users.forEach(user => {
          if (user.activePass) {
            const purchaseDate = user.activePass.purchaseDate;
            if (purchaseDate && purchaseDate >= start && purchaseDate <= end) {
              // Cargar precio del bono desde passTypes si está disponible
              // Por ahora asumimos un precio base si no está disponible
              monthlyRevenue += user.activePass.price || 0;
            }
          }
        });

        // 3. Contar clases realizadas este mes (clases que ya pasaron)
        const classesQuery = query(
          collection(db, 'classes'),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const classesSnapshot = await getDocs(classesQuery);

        let completedClasses = 0;
        let waitlistTotal = 0;
        const now = new Date();

        classesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const classDate = data.date;
          const classTime = data.time || '00:00';

          // Verificar si la clase es de este mes
          if (classDate >= start && classDate <= end) {
            // Verificar si la clase ya pasó
            const classDateTime = new Date(`${classDate}T${classTime}`);
            if (classDateTime < now) {
              completedClasses++;
            }

            // Contar personas en lista de espera
            const waitlist = data.waitlist || [];
            waitlistTotal += waitlist.length;
          }
        });

        // 4. Contar chats pendientes (mensajes no leídos de usuarios)
        const chatsQuery = query(
          collection(db, 'chats'),
          where('businessId', '==', businessId),
          where('senderRole', '==', 'user'),
          where('isRead', '==', false)
        );
        const chatsSnapshot = await getDocs(chatsQuery);

        // Contar usuarios únicos con mensajes pendientes
        const uniqueUsers = new Set<string>();
        chatsSnapshot.docs.forEach(doc => {
          const senderId = doc.data().senderId;
          if (senderId) uniqueUsers.add(senderId);
        });

        // 5. Contar usuarios pendientes de aprobación
        const pendingUsersQuery = query(
          collection(db, 'users'),
          where('businessId', '==', businessId),
          where('status', '==', 'pending_approval')
        );
        const pendingUsersSnapshot = await getDocs(pendingUsersQuery);

        setStats({
          totalUsers: users.length,
          monthlyRevenue,
          completedClasses,
          waitlistTotal
        });
        setPendingChats(uniqueUsers.size);
        setPendingApprovals(pendingUsersSnapshot.size);
      } catch (error) {
        console.error('Error cargando datos del dashboard:', error);
      }
    };

    loadData();
    // Refrescar cada 30 segundos
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [businessId]);

  const menuItems = [
    {
      title: 'Usuarios',
      description: 'Gestiona los usuarios y sus membresías',
      icon: Users,
      href: '/admin/users',
      color: 'from-blue-500 to-blue-600',
      stat: stats.totalUsers,
      statLabel: 'usuarios registrados'
    },
    {
      title: 'Instructores',
      description: 'Administra el equipo de instructores',
      icon: UserCheck,
      href: '/admin/instructors',
      color: 'from-green-500 to-green-600',
      stat: stats.totalInstructors,
      statLabel: 'instructores'
    },
    {
      title: 'Clases',
      description: 'Configura los tipos de clases',
      icon: BookOpen,
      href: '/admin/class-types',
      color: 'from-purple-500 to-purple-600',
      stat: stats.totalClasses,
      statLabel: 'tipos de clase'
    },
    {
      title: 'Membresías y Bonos',
      description: 'Gestiona planes y bonos de clases',
      icon: CreditCard,
      href: '/admin/class-passes',
      color: 'from-amber-500 to-amber-600'
    },
    {
      title: 'Calendario',
      description: 'Programa y gestiona el horario',
      icon: Calendar,
      href: '/admin/calendar-management',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      title: 'Anuncios',
      description: 'Publica noticias y comunicados',
      icon: Megaphone,
      href: '/admin/announcements',
      color: 'from-pink-500 to-pink-600'
    },
    {
      title: 'Lector QR',
      description: 'Escanea códigos de usuarios',
      icon: ScanLine,
      href: '/admin/qr-reader',
      color: 'from-indigo-500 to-indigo-600'
    },
    {
      title: 'WODs',
      description: 'Publica entrenamientos del día',
      icon: Dumbbell,
      href: '/admin/wods',
      color: 'from-red-500 to-red-600'
    },
    {
      title: 'Configuración',
      description: 'Personaliza tu negocio',
      icon: Settings,
      href: '/admin/config',
      color: 'from-gray-500 to-gray-600'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard - {currentBusiness?.name || 'Admin'}</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-amber-400">
            Panel de Administración
          </h1>
          <p className="text-gray-400 mt-2">
            Bienvenido al área de administración de {currentBusiness?.name || 'tu negocio'}
          </p>
        </div>

        {/* Alerta de Chats Pendientes */}
        {pendingChats > 0 && (
          <Link
            to="/admin/users"
            className="block bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-700 rounded-2xl p-4 hover:from-red-900/70 hover:to-orange-900/70 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-7 h-7 text-white" />
                </div>
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                  {pendingChats}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">
                  {pendingChats === 1 ? 'Tienes 1 chat pendiente' : `Tienes ${pendingChats} chats pendientes`}
                </h3>
                <p className="text-red-300 text-sm">
                  Hay usuarios esperando tu respuesta. Haz clic para ver los mensajes.
                </p>
              </div>
              <div className="text-red-400">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
        )}

        {/* Alerta de Usuarios Pendientes de Aprobación */}
        {pendingApprovals > 0 && (
          <Link
            to="/admin/users?status=pending_approval"
            className="block bg-gradient-to-r from-yellow-900/50 to-amber-900/50 border border-yellow-700 rounded-2xl p-4 hover:from-yellow-900/70 hover:to-amber-900/70 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 bg-yellow-600 rounded-xl flex items-center justify-center">
                  <UserPlus className="w-7 h-7 text-white" />
                </div>
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                  {pendingApprovals}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">
                  {pendingApprovals === 1 ? 'Tienes 1 usuario pendiente de aprobación' : `Tienes ${pendingApprovals} usuarios pendientes de aprobación`}
                </h3>
                <p className="text-yellow-300 text-sm">
                  Hay nuevos usuarios esperando ser aprobados. Haz clic para revisarlos.
                </p>
              </div>
              <div className="text-yellow-400">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
        )}

        {/* Estadísticas rápidas del mes */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Usuarios totales */}
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                <p className="text-xs text-gray-500">Usuarios</p>
              </div>
            </div>
          </div>

          {/* Ingresos del mes */}
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                <Euro className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.monthlyRevenue.toFixed(0)}€</p>
                <p className="text-xs text-gray-500">Ingresos mes</p>
              </div>
            </div>
          </div>

          {/* Clases realizadas este mes */}
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.completedClasses}</p>
                <p className="text-xs text-gray-500">Clases mes</p>
              </div>
            </div>
          </div>

          {/* Lista de espera total del mes */}
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-600/20 rounded-lg flex items-center justify-center">
                <UserX className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.waitlistTotal}</p>
                <p className="text-xs text-gray-500">En lista espera</p>
              </div>
            </div>
          </div>
        </div>

        {/* Menú de acceso rápido */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {menuItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="group bg-gray-900 rounded-2xl p-5 border border-gray-800 hover:border-gray-700 transition-all hover:shadow-lg hover:shadow-black/20"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">
                {item.title}
              </h3>
              <p className="text-sm text-gray-500">
                {item.description}
              </p>
              {item.stat !== undefined && (
                <p className="text-xs text-gray-600 mt-2">
                  {item.stat} {item.statLabel}
                </p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

export default AdminDashboardPage;
